import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, X, CheckCircle2, KeyRound, Search, Eye, EyeOff } from "lucide-react";

const ADMIN_PASSWORD = 'Unicorn2025!';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SOURCE_LABELS = {
    purchase: 'Purchase',
    summit_2026_speaker: '2026 speaker',
    summit_2026_attendee: '2026 attendee',
    admin_grant: 'Admin grant',
};
const GRANT_SOURCES = [
    { value: 'summit_2026_speaker', label: '2026 Summit speaker (free access)' },
    { value: 'admin_grant', label: 'Other comp / admin grant' },
];
const PERSONA_LABELS = { host: 'Host', creator: 'Creator', both: 'Both' };
const CONTRIBUTOR_STATUSES = ['new', 'contacted', 'accepted', 'declined'];

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatAmount = (cents) => {
    if (cents == null) return '—';
    return `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    })}`;
};
const csvEscape = (value) => {
    const str = String(value ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};
const downloadCsv = (filename, header, rows) => {
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const postAdmin = async (payload) => {
    const res = await fetch('/api/admin/academy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminPassword: ADMIN_PASSWORD, ...payload }),
    });
    return res.json();
};

const VIEWS = [
    { key: 'access', label: 'Access' },
    { key: 'waitlist', label: 'Notify list' },
    { key: 'contributors', label: 'Contributors' },
];

const AcademyAdmin = () => {
    const [overview, setOverview] = useState(null);
    const [entitlements, setEntitlements] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [contributors, setContributors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [view, setView] = useState('access');
    const [modeSaving, setModeSaving] = useState(false);

    // Grant modal
    const [grantOpen, setGrantOpen] = useState(false);
    const [grantForm, setGrantForm] = useState({ email: '', packageSlug: 'summit-2026-full', source: 'summit_2026_speaker', note: '' });
    const [grantSaving, setGrantSaving] = useState(false);
    const [grantError, setGrantError] = useState('');
    const [granted, setGranted] = useState(null);

    // Lookup
    const [lookupEmail, setLookupEmail] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupBusy, setLookupBusy] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [ov, ent, wl, co] = await Promise.all([
                postAdmin({ action: 'overview' }),
                postAdmin({ action: 'entitlements' }),
                postAdmin({ action: 'waitlist' }),
                postAdmin({ action: 'contributors' }),
            ]);
            if (ov.ok) setOverview(ov); else setLoadError('Failed to load Academy overview.');
            if (ent.ok) setEntitlements(ent.entitlements || []);
            if (wl.ok) setWaitlist(wl.waitlist || []);
            if (co.ok) setContributors(co.contributors || []);
        } catch (err) {
            console.error('Error loading academy admin:', err);
            setLoadError('Failed to load Academy data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mode = overview?.mode || 'coming_soon';
    const counts = overview?.counts || {};
    const packages = overview?.packages || [];

    const setMode = async (next) => {
        if (next === mode) return;
        if (next === 'live' && !window.confirm('Go live? Members will be able to play courses and buy Summit packages. You can switch back to Coming soon at any time.')) return;
        setModeSaving(true);
        try {
            const data = await postAdmin({ action: 'setMode', mode: next });
            if (data.ok) setOverview((o) => ({ ...o, mode: data.mode }));
        } finally {
            setModeSaving(false);
        }
    };

    const handleExport = () => {
        const stamp = new Date().toISOString().slice(0, 10);
        if (view === 'waitlist') {
            downloadCsv(`academy-notify-list-${stamp}.csv`, ['Email', 'Persona', 'Attended 2026', 'Source', 'Date'],
                waitlist.map((w) => [w.email, PERSONA_LABELS[w.persona] || '', w.attended2026 ? 'Yes' : 'No', w.source || '', w.createdAt ? new Date(w.createdAt).toISOString().slice(0, 10) : '']));
        } else if (view === 'contributors') {
            downloadCsv(`academy-contributors-${stamp}.csv`, ['Name', 'Email', 'Social', 'Topic', 'Has Content', 'Link', 'Status', 'Date'],
                contributors.map((c) => [c.name, c.email, c.socialHandle ? `@${c.socialHandle}` : '', c.topic, c.hasContent ? 'Yes' : 'No', c.contentUrl || '', c.status, c.createdAt ? new Date(c.createdAt).toISOString().slice(0, 10) : '']));
        } else {
            downloadCsv(`academy-access-${stamp}.csv`, ['Email', 'Name', 'Package', 'Source', 'Rate', 'Amount', 'Note', 'Date'],
                entitlements.map((e) => [e.email, e.buyerName || '', e.packageTitle, SOURCE_LABELS[e.source] || e.source, e.rate === 'attendee' ? 'Attendee' : e.source === 'purchase' ? 'Standard' : '', e.amountCents != null ? (e.amountCents / 100).toFixed(2) : '', e.note || '', e.createdAt ? new Date(e.createdAt).toISOString().slice(0, 10) : '']));
        }
    };

    const openGrant = () => {
        setGrantForm({ email: lookupResult?.email || '', packageSlug: 'summit-2026-full', source: 'summit_2026_speaker', note: '' });
        setGrantError('');
        setGranted(null);
        setGrantOpen(true);
    };

    const submitGrant = async (e) => {
        e.preventDefault();
        const email = grantForm.email.trim().toLowerCase();
        if (!EMAIL_REGEX.test(email)) { setGrantError('Enter a valid email.'); return; }
        setGrantSaving(true);
        setGrantError('');
        try {
            const data = await postAdmin({ action: 'grant', email, packageSlug: grantForm.packageSlug, source: grantForm.source, note: grantForm.note.trim() });
            if (data.ok && data.entitlement) {
                setEntitlements((prev) => [data.entitlement, ...prev.filter((x) => x.id !== data.entitlement.id)]);
                setGranted(data.entitlement);
            } else {
                setGrantError('Could not grant access. Please try again.');
            }
        } catch (err) {
            setGrantError('Could not grant access. Please try again.');
        } finally {
            setGrantSaving(false);
        }
    };

    const revoke = async (row) => {
        if (!window.confirm(`Remove ${row.packageTitle} from ${row.email}?`)) return;
        const data = await postAdmin({ action: 'revoke', id: row.id });
        if (data.ok) setEntitlements((prev) => prev.filter((x) => x.id !== row.id));
    };

    const runLookup = async (e) => {
        e?.preventDefault();
        const email = lookupEmail.trim().toLowerCase();
        if (!EMAIL_REGEX.test(email)) return;
        setLookupBusy(true);
        try {
            const data = await postAdmin({ action: 'lookup', email });
            setLookupResult(data.ok ? data : null);
        } finally {
            setLookupBusy(false);
        }
    };

    const setContributorStatus = async (row, status) => {
        setContributors((prev) => prev.map((c) => (c.id === row.id ? { ...c, status } : c)));
        await postAdmin({ action: 'setContributorStatus', id: row.id, status });
    };

    const viewCount = useMemo(() => ({
        access: entitlements.length,
        waitlist: waitlist.length,
        contributors: contributors.length,
    }), [entitlements, waitlist, contributors]);

    return (
        <>
            <div className="admin-section">
                <div className="sa-header">
                    <div>
                        <h2>Postaway Academy</h2>
                        <p className="sa-subtitle">
                            Summit recordings, packages and who can watch them. Live at <a href="/learn" target="_blank" rel="noopener noreferrer">/learn</a> (top nav “Resources”).
                        </p>
                    </div>
                    <div className="sa-header-actions">
                        <button type="button" className="sa-btn sa-btn-secondary" onClick={fetchAll} disabled={loading}>
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button type="button" className="sa-btn sa-btn-secondary" onClick={handleExport} disabled={loading || !viewCount[view]}>
                            <Download size={14} /> Export CSV
                        </button>
                        <button type="button" className="sa-btn sa-btn-primary" onClick={openGrant}>
                            <KeyRound size={14} /> Grant Access
                        </button>
                    </div>
                </div>

                <div className={`ac-mode ac-mode-${mode}`}>
                    <div className="ac-mode-copy">
                        <div className="ac-mode-label">
                            {mode === 'live' ? <Eye size={16} /> : <EyeOff size={16} />}
                            {mode === 'live' ? 'Live' : 'Coming soon'}
                        </div>
                        <p>
                            {mode === 'live'
                                ? 'Members can play courses; Summit recordings are locked behind packages. 2026 attendees pay the attendee rate automatically; speakers you grant here stream free.'
                                : 'Everyone sees the Academy layout, notify-me form and contributor CTA. Nothing plays and nothing can be bought yet.'}
                        </p>
                    </div>
                    <div className="sa-view-toggle" role="tablist" aria-label="Academy mode">
                        <button type="button" role="tab" aria-selected={mode === 'coming_soon'} className={`sa-view-btn${mode === 'coming_soon' ? ' active' : ''}`} disabled={modeSaving} onClick={() => setMode('coming_soon')}>
                            Coming soon
                        </button>
                        <button type="button" role="tab" aria-selected={mode === 'live'} className={`sa-view-btn${mode === 'live' ? ' active' : ''}`} disabled={modeSaving} onClick={() => setMode('live')}>
                            Live
                        </button>
                    </div>
                </div>

                <div className="sa-stats sa-stats-5">
                    <div className="sa-stat"><span className="sa-stat-value">{counts.speakers ?? '—'}</span><span className="sa-stat-label">Speakers (free access)</span></div>
                    <div className="sa-stat"><span className="sa-stat-value">{counts.attendees_2026 ?? '—'}</span><span className="sa-stat-label">2026 attendees (discount rate)</span></div>
                    <div className="sa-stat"><span className="sa-stat-value">{counts.purchases ?? '—'}{counts.attendee_purchases ? <small className="ac-stat-new"> · {counts.attendee_purchases} attendee</small> : null}</span><span className="sa-stat-label">Packages sold</span></div>
                    <div className="sa-stat"><span className="sa-stat-value">{formatAmount(counts.revenue_cents ?? 0)}</span><span className="sa-stat-label">Package revenue</span></div>
                    <div className="sa-stat"><span className="sa-stat-value">{counts.waitlist ?? '—'}</span><span className="sa-stat-label">Notify list</span></div>
                </div>
                <p className="sa-subtitle ac-substat">
                    {counts.contributors ?? 0} contributor application{counts.contributors === 1 ? '' : 's'}{counts.contributors_new ? ` (${counts.contributors_new} new)` : ''} — see the Contributors tab below.
                </p>

                {packages.length > 0 && (
                    <div className="ac-packages">
                        {packages.map((p) => (
                            <div key={p.slug} className="ac-package">
                                <div className="ac-package-title">{p.title}</div>
                                <div className="ac-package-sub">{p.subtitle}</div>
                                <div className="ac-package-price">
                                    {formatAmount(p.price_cents)}
                                    {p.attendee_price_cents != null && <span className="ac-package-attendee"> · attendees {formatAmount(p.attendee_price_cents)}</span>}
                                </div>
                                <div className="ac-package-meta">
                                    {p.courses?.length || 0} course{p.courses?.length === 1 ? '' : 's'} · Stripe: standard {p.stripe_price_id ? '✓' : '✗'}, attendee {p.attendee_stripe_price_id ? '✓' : '✗'}
                                </div>
                            </div>
                        ))}
                        <div className="ac-package ac-package-note">
                            <div className="ac-package-title">Sessions</div>
                            <div className="ac-package-sub">
                                {counts.package_videos ?? 0} video{counts.package_videos === 1 ? '' : 's'} uploaded across {counts.package_courses ?? 0} Summit course{counts.package_courses === 1 ? '' : 's'}.
                                Videos are added to <code>learning_lessons</code> as Nathaniel delivers them.
                            </div>
                        </div>
                    </div>
                )}

                <form className="ac-lookup" onSubmit={runLookup}>
                    <Search size={16} />
                    <input
                        type="email"
                        value={lookupEmail}
                        onChange={(e) => { setLookupEmail(e.target.value); setLookupResult(null); }}
                        placeholder="Check an email — does this person have access?"
                    />
                    <button type="submit" className="sa-btn sa-btn-secondary" disabled={lookupBusy || !EMAIL_REGEX.test(lookupEmail.trim())}>
                        {lookupBusy ? 'Checking…' : 'Check'}
                    </button>
                </form>
                {lookupResult && (
                    <div className="ac-lookup-result">
                        <strong>{lookupResult.email}</strong>
                        {' · '}
                        {lookupResult.hasAccount ? (lookupResult.hasPassword ? 'Has a Postaway login' : 'Account exists, no password yet') : 'No Postaway account yet'}
                        {' · '}
                        {lookupResult.isAttendee ? '2026 attendee (discount rate)' : 'Not on the 2026 attendee list'}
                        {' · '}
                        {lookupResult.entitlements?.length
                            ? lookupResult.entitlements.map((e) => `${e.packageSlug.replace('summit-2026-', '')} (${SOURCE_LABELS[e.source] || e.source})`).join(', ')
                            : 'No access'}
                        {!lookupResult.entitlements?.length && (
                            <button type="button" className="ac-inline-link" onClick={openGrant}>Grant access</button>
                        )}
                    </div>
                )}

                <div className="sa-view-toggle" role="tablist" aria-label="View">
                    {VIEWS.map((v) => (
                        <button key={v.key} type="button" role="tab" aria-selected={view === v.key} className={`sa-view-btn${view === v.key ? ' active' : ''}`} onClick={() => setView(v.key)}>
                            {v.label}
                            <span className="sa-view-count">{viewCount[v.key]}</span>
                        </button>
                    ))}
                </div>

                {loadError && <div className="admin-error">{loadError}</div>}

                {loading ? (
                    <p className="sa-empty">Loading…</p>
                ) : view === 'access' ? (
                    entitlements.length === 0 ? (
                        <p className="sa-empty">
                            No purchases or speaker grants yet. Use “Grant Access” to give each 2026 speaker free access.
                            Attendees don’t need a row — anyone on the Events → Summit 2026 list gets the attendee rate at checkout.
                        </p>
                    ) : (
                        <div className="ca-table-wrap">
                            <table className="ca-table">
                                <thead>
                                    <tr><th>Email</th><th>Name</th><th>Package</th><th>Source</th><th>Rate</th><th>Paid</th><th>Note</th><th>Date</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {entitlements.map((e) => (
                                        <tr key={e.id} className="ca-row" style={{ cursor: 'default' }}>
                                            <td><a className="sa-email" href={`mailto:${e.email}`}>{e.email}</a></td>
                                            <td>{e.buyerName || '—'}</td>
                                            <td>{e.packageTitle}</td>
                                            <td><span className={`sa-badge ac-badge-${e.source}`}>{SOURCE_LABELS[e.source] || e.source}</span></td>
                                            <td>{e.source === 'purchase' ? (e.rate === 'attendee' ? 'Attendee' : 'Standard') : '—'}</td>
                                            <td>{formatAmount(e.amountCents)}</td>
                                            <td className="sa-cell-topic">{e.note ? <div className="sa-speaker-topic" title={e.note}>{e.note}</div> : '—'}</td>
                                            <td>{formatDate(e.createdAt)}</td>
                                            <td>
                                                {e.source !== 'purchase' && (
                                                    <button type="button" className="ac-inline-link ac-inline-danger" onClick={() => revoke(e)}>Remove</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : view === 'waitlist' ? (
                    waitlist.length === 0 ? (
                        <p className="sa-empty">Nobody on the notify list yet. Sign-ups from the coming-soon page appear here.</p>
                    ) : (
                        <div className="ca-table-wrap">
                            <table className="ca-table">
                                <thead><tr><th>Email</th><th>I'm a</th><th>Attended 2026</th><th>Source</th><th>Date</th></tr></thead>
                                <tbody>
                                    {waitlist.map((w) => (
                                        <tr key={w.id} className="ca-row" style={{ cursor: 'default' }}>
                                            <td><a className="sa-email" href={`mailto:${w.email}`}>{w.email}</a></td>
                                            <td>{w.persona ? <span className={`sa-badge sa-badge-${w.persona === 'both' ? 'early-bird' : w.persona}`}>{PERSONA_LABELS[w.persona]}</span> : '—'}</td>
                                            <td>{w.attended2026 ? <span className="sa-badge sa-badge-speaker">Yes</span> : '—'}</td>
                                            <td>{w.source || '—'}</td>
                                            <td>{formatDate(w.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : contributors.length === 0 ? (
                    <p className="sa-empty">No contributor applications yet. “Apply to contribute” on the Academy page lands here.</p>
                ) : (
                    <div className="ca-table-wrap">
                        <table className="ca-table">
                            <thead><tr><th>Name</th><th>Email</th><th>Social</th><th>Would teach</th><th>Content</th><th>Status</th><th>Date</th></tr></thead>
                            <tbody>
                                {contributors.map((c) => (
                                    <tr key={c.id} className="ca-row" style={{ cursor: 'default' }}>
                                        <td className="sa-cell-name">{c.name}</td>
                                        <td><a className="sa-email" href={`mailto:${c.email}`}>{c.email}</a></td>
                                        <td>{c.socialHandle ? `@${c.socialHandle}` : '—'}</td>
                                        <td className="sa-cell-topic"><div className="sa-speaker-topic sa-speaker-topic-wide" title={c.topic}>{c.topic}</div></td>
                                        <td>
                                            {c.hasContent ? (c.contentUrl ? <a className="sa-email" href={c.contentUrl} target="_blank" rel="noopener noreferrer">Yes ↗</a> : 'Yes') : 'Not yet'}
                                        </td>
                                        <td>
                                            <select className="ac-status" value={c.status} onChange={(e) => setContributorStatus(c, e.target.value)}>
                                                {CONTRIBUTOR_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                                            </select>
                                        </td>
                                        <td>{formatDate(c.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {grantOpen && (
                <div className="sa-modal-overlay" onClick={() => !grantSaving && setGrantOpen(false)}>
                    <div className="sa-modal" role="dialog" aria-modal="true" aria-labelledby="ac-grant-title" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="sa-modal-close" onClick={() => setGrantOpen(false)} disabled={grantSaving} aria-label="Close"><X size={18} /></button>
                        {granted ? (
                            <div className="sa-modal-success">
                                <CheckCircle2 size={44} className="sa-modal-success-icon" />
                                <h3 id="ac-grant-title">Access granted</h3>
                                <p><strong>{granted.email}</strong> now has <strong>{granted.packageTitle}</strong> ({SOURCE_LABELS[granted.source] || granted.source}). They can log in with that email — or use “I'm a speaker — claim my access” on the Academy page to set a password.</p>
                                <div className="sa-modal-actions">
                                    <button type="button" className="sa-btn sa-btn-secondary" onClick={openGrant}>Grant another</button>
                                    <button type="button" className="sa-btn sa-btn-primary" onClick={() => setGrantOpen(false)}>Close</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h3 id="ac-grant-title" className="sa-modal-title">Grant Academy access</h3>
                                <p className="sa-subtitle">Free access for 2026 speakers (one at a time, by email), plus any other comps. No email is sent — they claim access on the Academy page.</p>
                                <form className="sa-form" onSubmit={submitGrant}>
                                    <div className="sa-form-grid sa-form-grid-modal">
                                        <label className="sa-form-field sa-form-field-wide">
                                            <span>Email *</span>
                                            <input type="email" value={grantForm.email} onChange={(e) => setGrantForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@example.com" required autoFocus />
                                        </label>
                                        <label className="sa-form-field sa-form-field-wide">
                                            <span>Reason *</span>
                                            <select value={grantForm.source} onChange={(e) => setGrantForm((f) => ({ ...f, source: e.target.value }))}>
                                                {GRANT_SOURCES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                            </select>
                                        </label>
                                        <label className="sa-form-field sa-form-field-wide">
                                            <span>Package *</span>
                                            <select value={grantForm.packageSlug} onChange={(e) => setGrantForm((f) => ({ ...f, packageSlug: e.target.value }))}>
                                                {packages.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
                                            </select>
                                        </label>
                                        <label className="sa-form-field sa-form-field-wide">
                                            <span>Note (optional)</span>
                                            <input type="text" value={grantForm.note} onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Registered with work email" />
                                        </label>
                                    </div>
                                    {grantError && <div className="admin-error">{grantError}</div>}
                                    <div className="sa-modal-actions">
                                        <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setGrantOpen(false)} disabled={grantSaving}>Cancel</button>
                                        <button type="submit" className="sa-btn sa-btn-primary" disabled={grantSaving}><KeyRound size={14} />{grantSaving ? 'Granting…' : 'Grant access'}</button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default AcademyAdmin;
